package com.playoffpulse.shared.model

import kotlinx.serialization.Serializable

@Serializable
enum class LeagueId {
    NHL,
}

@Serializable
data class TeamRef(
    val id: String,
    val league: LeagueId,
    val city: String,
    val name: String,
    val abbreviation: String,
    val conference: String,
    val division: String,
    val logoUrl: String? = null,
) {
    val displayName: String
        get() = "$city $name"
}

@Serializable
data class TeamStanding(
    val teamId: String,
    val teamName: String,
    val conference: String,
    val division: String,
    val gamesPlayed: Int,
    val wins: Int,
    val losses: Int,
    val otLosses: Int,
    val points: Int,
    val regulationWins: Int? = null,
    val regulationPlusOtWins: Int? = null,
    val goalDifferential: Int? = null,
    val goalFor: Int? = null,
    val goalAgainst: Int? = null,
    val leagueSequence: Int? = null,
    val conferenceSequence: Int? = null,
    val divisionSequence: Int? = null,
    val wildcardSequence: Int? = null,
) {
    val gamesRemaining: Int
        get() = (MAX_GAMES - gamesPlayed).coerceAtLeast(0)

    val maxPossiblePoints: Int
        get() = points + (gamesRemaining * 2)

    companion object {
        private const val MAX_GAMES = 82
    }
}

@Serializable
data class SnapshotMeta(
    val source: String,
    val generatedAtIso: String,
    val asOfLabel: String,
    val staleAfterMinutes: Int = 1_440,
    val refreshCadence: String = "Nightly at 5:00 AM America/New_York",
)

@Serializable
data class StandingsSnapshot(
    val league: LeagueId,
    val standings: List<TeamStanding>,
    val meta: SnapshotMeta,
)

@Serializable
enum class MagicStatus {
    CONTROLS_DESTINY,
    CHASING,
    IN_POSITION,
    CLINCHED,
    ELIMINATED,
}

@Serializable
enum class ImportantGamesWindow {
    TODAY,
    WEEK,
    REMAINING,
}

@Serializable
enum class ImportantGamesObjective {
    PLAYOFF,
    DRAFT,
}

@Serializable
enum class ImportanceTier {
    IMMEDIATE,
    FUTURE,
}

@Serializable
data class ImportantGameEntry(
    val gameId: Long,
    val startTimeIso: String,
    val gameState: String,
    val awayTeamId: String,
    val homeTeamId: String,
    val importanceTier: ImportanceTier,
    val swingScore: Int,
    val explanation: String,
)

@Serializable
data class ImportantGamesResponse(
    val league: LeagueId,
    val teamId: String,
    val objective: ImportantGamesObjective = ImportantGamesObjective.PLAYOFF,
    val objectivePath: String,
    val draftTargetTag: String? = null,
    val window: ImportantGamesWindow,
    val windowStartEtIso: String,
    val windowEndEtIso: String,
    val snapshotGeneratedAtIso: String,
    val importantGames: List<ImportantGameEntry>,
)

@Serializable
enum class YesterdayMovementSignificanceBucket {
    STATUS_CHANGE,
    SEAT_CHANGE,
    MAGIC_SHIFT,
    TRAGIC_SHIFT,
    POINTS_ONLY,
}

@Serializable
data class YesterdayMovementEntry(
    val team: TeamRef,
    val previousStatus: MagicStatus,
    val currentStatus: MagicStatus,
    val previousSeatCode: String? = null,
    val currentSeatCode: String? = null,
    val magicPointsDelta: Int,
    val tragicPointsDelta: Int,
    val pointsDelta: Int,
    val headline: String,
    val significanceBucket: YesterdayMovementSignificanceBucket,
    val significanceScore: Int,
)

@Serializable
data class YesterdayMovementResponse(
    val league: LeagueId,
    val available: Boolean,
    val latestSnapshotGeneratedAtIso: String? = null,
    val comparedSnapshotDateEt: String? = null,
    val movement: List<YesterdayMovementEntry> = emptyList(),
)

@Serializable
data class MagicSummary(
    val league: LeagueId,
    val team: TeamRef,
    val status: MagicStatus,
    val playoffPositionCode: String? = null,
    val playoffPositionLabel: String? = null,
    val presidentsCupTag: String? = null,
    val conferenceHomeIceTag: String? = null,
    val highestClinchedSeatCode: String? = null,
    val nextClinchTargetSeatCode: String? = null,
    val currentPoints: Int,
    val maxPossiblePoints: Int,
    val conferenceCutLinePoints: Int,
    val clinchTargetPoints: Int,
    val magicPointsToClinch: Int,
    val lowestAttainableClinchWins: Int? = null,
    val lowestAttainableClinchWinsAsterisk: Boolean = false,
    val lowestAttainableClinchWinsNote: String? = null,
    val bestCaseClinchWins: Int? = null,
    val bestCaseClinchPoints: Int? = null,
    val bestCaseClinchDateEt: String? = null,
    val bestCaseClinchNote: String? = null,
    val bestCaseScenarioAvailable: Boolean = false,
    val cancellationPoolPoints: Int? = null,
    val tragicPointsToElimination: Int,
    val computedAtIso: String,
    val note: String,
)

@Serializable
data class BestCaseClinchWitnessResult(
    val gameId: Long,
    val startTimeIso: String,
    val awayTeamId: String,
    val homeTeamId: String,
    val requiredWinnerTeamId: String,
    val regulationOnly: Boolean = false,
)

@Serializable
data class BestCaseClinchWitnessGroup(
    val dateEt: String,
    val results: List<BestCaseClinchWitnessResult>,
)

@Serializable
data class BestCaseClinchTiebreakNote(
    val rivalTeam: TeamRef,
    val detail: String,
)

@Serializable
data class BestCaseClinchResponse(
    val league: LeagueId,
    val team: TeamRef,
    val snapshotGeneratedAtIso: String,
    val conservativeClinchPoints: Int,
    val conservativeClinchWins: Int,
    val bestCaseClinchPoints: Int? = null,
    val bestCaseClinchWins: Int? = null,
    val bestCaseClinchDateEt: String? = null,
    val bestCaseSeatCode: String? = null,
    val bestCaseSeatLabel: String? = null,
    val bestCaseClinchNote: String? = null,
    val scenarioAvailable: Boolean = false,
    val witnessGroups: List<BestCaseClinchWitnessGroup> = emptyList(),
    val tiebreakNotes: List<BestCaseClinchTiebreakNote> = emptyList(),
)

@Serializable
enum class RivalTiebreakState {
    POINTS_ONLY,
    SELECTED_EDGE,
    RIVAL_EDGE,
    UNRESOLVED,
}

@Serializable
data class TopRivalEntry(
    val team: TeamRef,
    val seatCode: String? = null,
    val currentPoints: Int,
    val maxPossiblePoints: Int,
    val pressurePoints: Int,
    val holdsCutoffSeat: Boolean,
    val drivesClinchPoints: Boolean = false,
    val drivesClinchWins: Boolean = false,
    val tiebreakState: RivalTiebreakState,
    val explanation: String,
)

@Serializable
data class TopRivalsResponse(
    val league: LeagueId,
    val teamId: String,
    val snapshotGeneratedAtIso: String,
    val targetSeatLabel: String,
    val cutoffHolder: TeamRef? = null,
    val cutoffHolderSeatCode: String? = null,
    val topRivals: List<TopRivalEntry>,
)

@Serializable
data class VanquishedEntry(
    val team: TeamRef,
    val currentPoints: Int,
    val maxPossiblePoints: Int,
    val tiedOnPoints: Boolean = false,
    val reason: String,
    val tiebreakDetail: String? = null,
    val publishedSignalPath: String? = null,
)

@Serializable
data class VanquishProgressSlot(
    val team: TeamRef? = null,
    val state: VanquishProgressSlotState,
)

@Serializable
enum class VanquishProgressSlotState {
    FILLED,
    NEXT_UP,
    EMPTY,
}

@Serializable
data class VanquishProgressRow(
    val seatCode: String,
    val totalSlots: Int,
    val filledCount: Int,
    val slots: List<VanquishProgressSlot>,
)

@Serializable
data class VanquishProgressSummary(
    val rows: List<VanquishProgressRow> = emptyList(),
    val conference: VanquishProgressRow? = null,
    val division: VanquishProgressRow? = null,
    val highestClinchedSeatCode: String? = null,
    val nextClinchTargetSeatCode: String? = null,
)

fun VanquishProgressSummary.displayRows(): List<VanquishProgressRow> {
    return if (rows.isNotEmpty()) rows else listOfNotNull(conference, division)
}

@Serializable
data class VanquishedResponse(
    val league: LeagueId,
    val teamId: String,
    val snapshotGeneratedAtIso: String,
    val progress: VanquishProgressSummary? = null,
    val vanquished: List<VanquishedEntry>,
)

@Serializable
data class DraftRivalEntry(
    val team: TeamRef,
    val currentPoints: Int,
    val maxPossiblePoints: Int,
    val currentDraftSlot: Int,
    val reason: String,
)

@Serializable
data class DraftLockedBelowEntry(
    val team: TeamRef,
    val currentPoints: Int,
    val maxPossiblePoints: Int,
    val tiedOnPoints: Boolean = false,
    val reason: String,
    val tiebreakDetail: String? = null,
    val publishedSignalPath: String? = null,
)

@Serializable
data class DraftBestCaseWitnessResult(
    val gameId: Long,
    val startTimeIso: String,
    val awayTeamId: String,
    val homeTeamId: String,
    val requiredWinnerTeamId: String,
)

@Serializable
data class DraftBestCaseWitnessGroup(
    val dateEt: String,
    val results: List<DraftBestCaseWitnessResult>,
)

@Serializable
data class DraftStatusProgressSummary(
    val rows: List<VanquishProgressRow> = emptyList(),
    val highestLockedDraftTag: String? = null,
    val nextTargetDraftTag: String? = null,
)

@Serializable
data class DraftWatchResponse(
    val league: LeagueId,
    val teamId: String,
    val snapshotGeneratedAtIso: String,
    val active: Boolean,
    val conditionalOnMissingPlayoffs: Boolean = false,
    val currentDraftSlot: Int? = null,
    val currentDraftSlotLabel: String? = null,
    val currentDraftTag: String? = null,
    val bestPossibleDraftSlot: Int? = null,
    val bestPossibleDraftSlotLabel: String? = null,
    val worstPossibleDraftSlot: Int? = null,
    val worstPossibleDraftSlotLabel: String? = null,
    val nextBetterDraftTag: String? = null,
    val betterTagLinePoints: Int? = null,
    val pointsToBetterTag: Int? = null,
    val nextWorseDraftTag: String? = null,
    val pointsToSlip: Int? = null,
    val pathLabel: String? = null,
    val bestCaseScenarioAvailable: Boolean = false,
    val bestCaseDateEt: String? = null,
    val bestCaseTargetDraftTag: String? = null,
    val bestCaseNote: String? = null,
    val bestCaseWitnessGroups: List<DraftBestCaseWitnessGroup> = emptyList(),
    val statusProgress: DraftStatusProgressSummary? = null,
    val draftRivals: List<DraftRivalEntry> = emptyList(),
    val lockedBelow: List<DraftLockedBelowEntry> = emptyList(),
)

@Serializable
enum class TeamPerspective {
    PLAYOFF,
    DRAFT,
}

@Serializable
enum class DraftStandingsState {
    PLAYOFF_FIELD,
    PLAYOFF_CHASE,
    DRAFT,
}

@Serializable
data class DraftStandingsSummary(
    val state: DraftStandingsState,
    val currentTag: String? = null,
    val statusLabel: String,
    val nextGoalLabel: String? = null,
    val toBetterTagPoints: Int? = null,
    val betterTagLinePoints: Int? = null,
    val toSlipPoints: Int? = null,
    val pathLabel: String? = null,
)

@Serializable
data class StandingsInsightRow(
    val teamId: String,
    val playoffSummary: MagicSummary,
    val teamPerspective: TeamPerspective,
    val draftSummary: DraftStandingsSummary,
)

@Serializable
data class StandingsInsightsResponse(
    val league: LeagueId,
    val snapshotGeneratedAtIso: String,
    val rows: List<StandingsInsightRow>,
)

@Serializable
enum class BlockedShotsChartZone {
    ALL,
    OUTSIDE_SLOT,
    SLOT,
}

@Serializable
enum class BlockedShotsMissesMode {
    EXCLUDE,
    INCLUDE,
}

@Serializable
data class BlockedShotsChartRow(
    val team: TeamRef,
    val gamesPlayed: Int,
    val shotAttemptsAgainst: Int,
    val shotAttemptsAgainstPerGame: Double,
    val blockedAttempts: Int,
    val blockedAttemptsPerGame: Double,
    val unblockedAttemptsAgainst: Int,
    val unblockedAttemptsAgainstPerGame: Double,
    val blockShareOfShotAttempts: Double,
    val evenStrengthDefensiveZonePct: Double = 0.0,
    val evenStrengthDefensiveZoneRank: Int? = null,
    val evenStrengthDefensiveZoneLeagueAvg: Double = 0.0,
    val evenStrengthDefensiveZoneMinutesPer20: Double = 0.0,
    val evenStrengthDefensiveZoneLeagueAvgMinutesPer20: Double = 0.0,
    val blockedAttemptsPerTenPctDefensiveZone: Double = 0.0,
    val blockedAttemptsPerTwoDefensiveZoneMinutes: Double = 0.0,
)

@Serializable
data class BlockedShotsChartResponse(
    val league: LeagueId,
    val seasonId: String,
    val source: String,
    val fetchedAtIso: String,
    val snapshotGeneratedAtIso: String,
    val selectedZone: BlockedShotsChartZone,
    val selectedZoneLabel: String,
    val selectedMissesMode: BlockedShotsMissesMode,
    val selectedMissesModeLabel: String,
    val rows: List<BlockedShotsChartRow>,
)

@Serializable
data class BlockedShotsChartBundleResponse(
    val league: LeagueId,
    val seasonId: String,
    val source: String,
    val fetchedAtIso: String,
    val snapshotGeneratedAtIso: String,
    val selectedMissesMode: BlockedShotsMissesMode,
    val selectedMissesModeLabel: String,
    val allChart: BlockedShotsChartResponse,
    val outsideSlotChart: BlockedShotsChartResponse,
    val slotChart: BlockedShotsChartResponse,
)

@Serializable
data class BlockedShotsTeamGameRow(
    val gameId: Long,
    val sourceUrl: String,
    val startTimeIso: String,
    val gameState: String,
    val opponent: TeamRef,
    val isHome: Boolean,
    val teamScore: Int? = null,
    val opponentScore: Int? = null,
    val resultType: String? = null,
    val shotAttemptsAgainst: Int,
    val blockedAttempts: Int,
    val savedAttemptsAgainst: Int = 0,
    val missedAttemptsAgainst: Int = 0,
    val goalsAgainst: Int = 0,
    val unblockedAttemptsAgainst: Int,
    val blockShareOfShotAttempts: Double,
)

@Serializable
data class BlockedShotsTeamDetailResponse(
    val league: LeagueId,
    val seasonId: String,
    val source: String,
    val fetchedAtIso: String,
    val snapshotGeneratedAtIso: String,
    val selectedZone: BlockedShotsChartZone,
    val selectedZoneLabel: String,
    val selectedMissesMode: BlockedShotsMissesMode,
    val selectedMissesModeLabel: String,
    val team: TeamRef,
    val games: List<BlockedShotsTeamGameRow>,
)
